import { SilverbackPageContext } from '@amazeelabs/gatsby-source-silverback';
import {
  buildHtml,
  buildLink,
  buildUrl,
  ContentRoute,
  Link,
  RouteSlotInput,
} from '@caritas/ui';

import { useContentHubQuery } from '../../../generated/queries/react-queries';
import { useSiteSettings } from '../../commons/site-settings';
import { isDefined } from '../../utils/is-defined';
import { isProject } from '../../utils/is-project';
import { languageFallback } from '../../utils/localizations';
import { mapImage } from '../Image.mappers';
import { hasImage } from './ContentPageHeader.mappers';

type TeaserProps = Exclude<
  RouteSlotInput<typeof ContentRoute, 'body', 'teaserGrid'>['input'],
  Function
>['teasers'][0];

type TeaserList = BlockDynamicTeaserListFragment & {
  Link?: Link;
};

function isNotUndefinedOrNull<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}

const useTeasers = (
  input: TeaserList,
  pageContext: SilverbackPageContext,
): Teaser[] => {
  const highlightedTeasers =
    (input?.listing?.highlighted || [])
      .filter(isNotUndefinedOrNull)
      .map((contentPage) => contentPage.teaser) || [];

  let dynamicTeasers: Teaser[] = [];

  const dynamicTeasersResult = useContentHubQuery(
    {
      input: {
        langcode: languageFallback(pageContext.locale || 'de'),
        filters: {
          topics: input.listing?.topics.map((term) => term.termId) || [],
          types: input.listing?.format.map((term) => term.termId) || [],
          countries: input.listing?.country.map((term) => term.termId) || [],
        },
        pagination: {
          offset: 0,
          // We add one to the query to see if there are more matches than the limit using these filter
          // We then slice the array to match the limit in mapTeasers
          limit: (input.listing?.totalItems || 0) + 1,
        },
        skipResults: false,
      },
    },
    {
      enabled:
        // Only run the query if we have filters
        (input.listing?.topics.length ||
          input.listing?.format.length ||
          input.listing?.country.length ||
          0) > 0,
    },
  );
  dynamicTeasers = dynamicTeasersResult.data
    ? (dynamicTeasersResult.data.contentHub.results.map(
        (contentPage) => contentPage.teaser,
      ) as Teaser[])
    : [];

  return [...highlightedTeasers, ...(dynamicTeasers || [])] as Teaser[];
};

const mapTeasers = (
  teasers: Teaser[],
  input: TeaserList,
): Array<TeaserProps> => {
  const showDescriptions = input.showDescriptions;
  const limit = input.listing?.totalItems || 0;
  return (
    teasers
      // Filter out potential nulls first
      ?.filter(isDefined)
      .map((teaser) => {
        // We know this won't be null, so force typing here
        const image = hasImage(teaser.image)
          ? mapImage({
              image: teaser.image.image,
              preset: 'teaser',
              sizes: '(max-width: 1023px) 900px, 300px',
              caption: teaser.image.caption,
            })
          : undefined;
        return {
          title: teaser.title,
          Link: buildLink({ href: teaser.url }),
          language: teaser.language,
          Image: image,
          date: teaser.date,
          description: showDescriptions ? teaser?.description : undefined,
          sosTag:
            teaser?.headerDisplayMode === 'disaster_relief' ||
            !!teaser.sosLocation,
          bioTag: !!teaser.bioLocation,
          status: teaser.status,
          isProject: isProject(teaser.format),
        };
      })
      .slice(0, limit) || []
  );
};

const layoutMap = {
  carousel: 'teaserSlideshow',
  promo: 'teaserPromoLayout',
  grid: 'teaserGrid',
} as const;

export const mapDynamicTeaserList = (pageContext: SilverbackPageContext) =>
  function (
    input: TeaserList,
  ):
    | RouteSlotInput<typeof ContentRoute, 'body', 'teaserGrid'>
    | RouteSlotInput<typeof ContentRoute, 'body', 'teaserPromoLayout'>
    | RouteSlotInput<typeof ContentRoute, 'body', 'teaserSlideshow'> {
    const blockLayout = input.display || 'carousel';
    return {
      key: layoutMap[blockLayout],
      input: function useDynamicTeaserList() {
        const { contentHubPagePath } = useSiteSettings(pageContext);
        const filters = JSON.stringify({
          themes: input.listing?.topics.map((term) => term.termId) || [],
          types: input.listing?.format.map((term) => term.termId) || [],
          countries: input.listing?.country.map((term) => term.termId) || [],
        });
        const hasFilters =
          input.listing?.topics.length ||
          input.listing?.format.length ||
          input.listing?.country.length;
        const ContentHubLink = buildLink({
          href: buildUrl([contentHubPagePath], { filters }),
        });
        const teasers = useTeasers(input, pageContext);
        return [
          {
            Title: input.injectedHeading
              ? buildHtml(input.injectedHeading)
              : undefined,
            Link:
              input.injectedHeading && hasFilters ? ContentHubLink : undefined,
            teasers: mapTeasers(teasers, input),
            MoreLinkTitle: input?.listing?.moreLinkText,
            MoreLink: hasFilters ? ContentHubLink : undefined,
          },
          200,
        ];
      },
    };
  };
